import { ReactElement } from 'react';
import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { Form, useFormik, FormikProvider } from 'formik';
import { useToasts } from 'react-toast-notifications';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import useBreadcrumb from 'packages/kakapo-ui-kit/components/breadcrumb/useBreadcrumb';
import { PageTitle, SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import MembersTable from 'packages/kakapo-ui-kit/components/common/MembersTable';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

function SettingsEditTeam(props: { team: graphql.Group }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();

	const formik = useFormik({
		initialValues: {
			name: props.team.name,
		},
		onSubmit: () => {},
	});

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading }] = graphql.useUpdateGroupMutation();

	const onUpdate = attempt(async () => {
		reset();

		await update({
			variables: {
				input: {
					id: props.team.id,
					name: formik.values.name,
					externalReferenceID: props.team.externalReferenceID ?? '',
				},
			},
		});

		addToast(t('common.settings.teams.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	return (
		<Flex flexDirection="column" mt={3}>
			<Flex flexDirection="row" alignItems="center">
				<PageTitle>{t('common.settings.teams.edit.title')}</PageTitle>
				<Box ml={3}>
					<Button size={32} onClick={() => history.push(`/team/${props.team.id}/goals`)}>
						{t('common.settings.teams.edit.viewGoals')}
					</Button>
				</Box>
			</Flex>
			<Flex mt={4} mb={2}>
				<SubSectionTitle>{t('common.settings.teams.create.details')}</SubSectionTitle>
			</Flex>
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: 'Name',
								name: 'name',
								type: DetailsTableFormValueTypes.Text,
							},
						]}
					/>
				</Form>
			</FormikProvider>

			<Flex mt={3}>
				<Button size={32} onClick={onUpdate} isLoading={loading}>
					{t('common.save')}
				</Button>
			</Flex>

			<Box mt={5}>
				<MembersTable group={props.team} />
			</Box>
		</Flex>
	);
}

function SettingsEditTeamID(props: { id: string }): ReactElement {
	const { t } = useTranslation();

	useBreadcrumb(t('common.settings.teams.title'));

	const { data, error, loading } = graphql.useGroupQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return <Spinner isLoading />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <SettingsEditTeam team={data!.group as graphql.Group} />;
	}
}

export default SettingsEditTeamID;
